import React from 'react';
import type { VisitReasonColoredText } from 'types/anamnesis';
import { ensureString, formatReadableDate, parseBoldText } from './index';
import { colors } from 'styles/colors';

const MIDNIGHT_BLUE = colors.midnightBlue;
const VANTIS_VIOLET = colors.vantisViolet;

export function mapVisitReasonColor(color?: string | null): string {
  const normalized = ensureString(color).toLowerCase();
  if (normalized === 'blue' || normalized === 'midnight') {
    return MIDNIGHT_BLUE;
  }
  if (normalized === 'grey' || normalized === 'gray') {
    return VANTIS_VIOLET;
  }
  return ensureString(color);
}

export function buildVisitReasonTextNodes(
  text: string,
  colored: VisitReasonColoredText[],
  BoldComponent: React.ComponentType<React.PropsWithChildren<{ key?: string | number }>>,
  ColoredComponent: React.ComponentType<
    React.PropsWithChildren<{ $color: string; key?: string | number }>
  >
): React.ReactNode[] {
  if (!text || colored.length === 0) {
    return parseBoldText(text, BoldComponent);
  }

  const parts: React.ReactNode[] = [];
  let cursor = 0;
  const lowerText = text.toLowerCase();
  let colorKey = 0;

  colored.forEach((entry) => {
    const snippet = ensureString(entry.text).trim();
    if (!snippet) return;
    const matchIndex = lowerText.indexOf(snippet.toLowerCase(), cursor);
    if (matchIndex === -1) return;

    if (matchIndex > cursor) {
      const before = text.slice(cursor, matchIndex);
      parts.push(...parseBoldText(before, BoldComponent));
    }

    const matched = text.slice(matchIndex, matchIndex + snippet.length);
    const color = mapVisitReasonColor(entry.color);
    parts.push(
      React.createElement(
        ColoredComponent,
        { key: `colored-${colorKey++}`, $color: color },
        parseBoldText(matched, BoldComponent)
      )
    );

    cursor = matchIndex + snippet.length;
  });

  if (cursor < text.length) {
    parts.push(...parseBoldText(text.slice(cursor), BoldComponent));
  }

  return parts.length ? parts : parseBoldText(text, BoldComponent);
}

export function formatPatientName(patientName?: string): string {
  if (!patientName) return '';
  const normalizedName = ensureString(patientName).trim().replace(/\s+/g, ' ');
  const nameParts = normalizedName.split(' ').filter(Boolean);
  if (nameParts.length >= 2) {
    const lastName = nameParts[nameParts.length - 1];
    const rest = nameParts.slice(0, -1).join(' ');
    return `${lastName}, ${rest}`;
  }
  return normalizedName;
}

export function buildPatientNameLine(
  patientName?: string,
  birthdate?: string,
  medatixxPatientId?: string
): string {
  const patientParts: string[] = [];
  if (patientName) {
    patientParts.push(formatPatientName(patientName));
  }
  if (birthdate) patientParts.push(`*${formatReadableDate(birthdate)}`);
  if (medatixxPatientId) patientParts.push(`· #${medatixxPatientId}`);
  return patientParts.length > 0 ? patientParts.join(' ') : medatixxPatientId || 'Unbekannt';
}

export function buildPatientSubline(birthdate?: string, medatixxPatientId?: string): string {
  const patientParts: string[] = [];
  if (birthdate) patientParts.push(`*${formatReadableDate(birthdate)}`);
  if (medatixxPatientId) patientParts.push(`· #${medatixxPatientId}`);
  return patientParts.length > 0 ? patientParts.join(' ') : medatixxPatientId || 'Unbekannt';
}
